import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AuthBenefitsCarousel } from '@/features/auth/components/AuthBenefitsCarousel';
import { AuthLegalLinks } from '@/features/auth/components/AuthLegalLinks';
import { AuthThemeToggle } from '@/features/auth/components/AuthThemeToggle';
import { cn } from '@/lib/cn';

/** Public TruSpace marketing site — the "Book a Demo" destination. */
const EXPLORE_TRUSPACE_URL = 'https://exploretruspace.com/';

type AuthMarketingShellProps = {
  /** Form body; omit when `isLoading` renders the spinner instead. */
  children?: ReactNode;
  /** Optional form-card heading (shown above children on the form side). */
  title?: string;
  /** Optional supporting line under the title. */
  subtitle?: string;
  /** Optional links below the form, inside the card. */
  footer?: ReactNode;
  /** Extra class on the form card. */
  cardClassName?: string;
  /** When true, render a compact loading spinner instead of children. */
  isLoading?: boolean;
};

/**
 * Shared auth chrome for every pre-login surface (Login, Register, MFA,
 * Forgot/Reset/Set password, SSO callback).
 *
 * Two panels: a light marketing panel carrying the headline and the rotating
 * value props, and an SSI Deep Purple brand panel carrying the logo and the
 * form card. The brand panel reuses the sidebar chrome tokens because it is
 * the same kind of surface — brand identity, not page content — which also
 * means it resolves correctly in dark mode for free.
 *
 * On mobile the brand panel comes first so the form is reachable without
 * scrolling past the marketing copy.
 */
export function AuthMarketingShell({
  children,
  title,
  subtitle,
  footer,
  cardClassName,
  isLoading = false,
}: AuthMarketingShellProps) {
  return (
    <div className="flex min-h-screen flex-col bg-background lg:flex-row">
      <aside
        aria-label="Why teams use TruSpace"
        className="order-2 flex flex-col justify-center px-6 py-12 sm:px-10 lg:order-1 lg:w-[54%] lg:px-16 lg:py-16"
      >
        <div className="mx-auto w-full max-w-xl">
          <h1 className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl">
            The best home
            <span className="block text-primary">for your building data.</span>
          </h1>

          <div className="my-8 border-t border-border" />

          <AuthBenefitsCarousel
            action={
              <Button asChild variant="outline">
                <a
                  href={EXPLORE_TRUSPACE_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  Book a Demo
                </a>
              </Button>
            }
          />
        </div>
      </aside>

      <div className="relative order-1 isolate flex flex-col overflow-hidden bg-sidebar-bg px-6 py-10 sm:px-10 lg:order-2 lg:w-[46%] lg:px-12 lg:py-14">
        <BlueprintMotif />

        <div className="relative mx-auto flex w-full max-w-md flex-1 flex-col">
          <div className="flex items-start justify-between gap-4">
            <div>
              <img
                src="/TS_White.svg"
                alt="TruSpace"
                width={1293}
                height={257}
                className="h-8 w-auto sm:h-9"
              />
              <p className="mt-2 text-xs tracking-wide text-sidebar-text">
                by Stevenson Systems
              </p>
            </div>
            <AuthThemeToggle />
          </div>

          {/*
            `flex-1 items-center` is what vertically centers the card in the
            space between the logo and the legal rail, rather than pinning it
            under the logo or stretching it to the full panel height (DEF-250).
          */}
          <main className="auth-enter flex flex-1 items-center py-10">
            <div
              data-testid="auth-card"
              className={cn(
                'w-full rounded-xl border border-border bg-card p-7 text-card-foreground shadow-lg sm:p-8',
                cardClassName,
              )}
            >
              {(title || subtitle) && (
                <div className="mb-6">
                  {title ? (
                    <h2 className="text-xl font-semibold text-foreground">{title}</h2>
                  ) : null}
                  {subtitle ? (
                    <p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p>
                  ) : null}
                </div>
              )}

              {isLoading ? (
                <div className="flex justify-center py-8" aria-busy="true">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
                </div>
              ) : (
                children
              )}

              {footer ? (
                <div className="mt-6 border-t border-border pt-5 text-center text-sm text-muted-foreground">
                  {footer}
                </div>
              ) : null}
            </div>
          </main>

          {/* Brand / legal rail — company address lives here, never duplicated
              under the form footer (DEF-248). */}
          <div className="flex flex-col gap-2">
            <AuthLegalLinks />
            <address className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs not-italic text-sidebar-text">
              <span>Stevenson Systems, Inc.</span>
              <span aria-hidden="true">·</span>
              <span>27822 El Lazo Road, Laguna Niguel, CA 92677</span>
            </address>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Static drafting-line motif for the brand panel's lower corner. Deliberately
 * a flat inline SVG: gradients and blur filters are forbidden on
 * always-visible chrome because they repaint every scroll frame on the
 * integrated-graphics laptops this product ships to (`fe-performance.mdc` §11).
 */
function BlueprintMotif() {
  return (
    <svg
      className="pointer-events-none absolute -bottom-8 -right-8 -z-10 h-72 w-72 text-sidebar-text opacity-20"
      viewBox="0 0 200 200"
      fill="none"
      aria-hidden="true"
    >
      <g stroke="currentColor" strokeWidth="1">
        <path d="M0 40 L160 200M0 80 L120 200M0 120 L80 200M0 160 L40 200M40 0 L200 160M80 0 L200 120M120 0 L200 80M160 0 L200 40" />
      </g>
      <rect
        x="52"
        y="52"
        width="96"
        height="96"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
    </svg>
  );
}
